import { batch, signal } from "@preact/signals-react"

import { BACKEND_APIS } from "@constants"
import { makeClient } from "Pages/DsRepricing/@services/http"
import { fromApi, optionsFromApi, userFromApi } from "@components/IdentityEditor/@services/identity"


const userApi = makeClient(BACKEND_APIS.USER)

// What is saved (`null` = no identity yet). The screen only ever paints the server's answer (criteria 5, 7).
export const identity = signal(null)
// Whose identity: `{ uid, name }` of the session user (the slave's while a master views it)
export const identityUser = signal(null)
// The catalogues the pickers offer, from the same answer (`options`): `{ providers, sites }` as `{value, label, market}`
export const identityOptions = signal({ providers: [], sites: [] })
// 'idle' | 'loading' | 'ready' | 'error' | 'saving'
export const identityState = signal('idle')
// `{ status, code, message, field, ids }` of the last failed load or save
export const identityError = signal(null)
export const identitySavedAt = signal(null)

const errorOf = (e) => ({
	status: e?.status || null, code: e?.code || null, message: e?.message || null, field: e?.field ?? null, ids: e?.ids ?? null,
})

export async function loadIdentity({ force = false } = {}) {
	if (!force && (identityState.value === 'loading' || identityState.value === 'ready')) return
	batch(() => {
		identityState.value = 'loading'
		identityError.value = null
	})
	try {
		const data = await userApi.get('/identity')
		batch(() => {
			identity.value = fromApi(data)
			identityUser.value = userFromApi(data)
			identityOptions.value = optionsFromApi(data)
			identityState.value = 'ready'
		})
	} catch (e) {
		batch(() => {
			identityError.value = errorOf(e)
			identityState.value = 'error'
		})
	}
}

/** `PUT` the draft's payload; `true` when saved. Not optimistic: what is painted afterwards is the answer. */
export async function saveIdentity(payload) {
	identityState.value = 'saving'
	try {
		const data = await userApi.put('/identity', payload)
		batch(() => {
			identity.value = fromApi(data)
			if (data?.user) identityUser.value = userFromApi(data)
			identitySavedAt.value = new Date()
			identityError.value = null
			identityState.value = 'ready'
		})
		return true
	} catch (e) {
		batch(() => {
			identityError.value = errorOf(e)
			identityState.value = 'ready'
		})
		return false
	}
}

export const clearAccountSignals = () => batch(() => {
	identity.value = null
	identityUser.value = null
	identityOptions.value = { providers: [], sites: [] }
	identityState.value = 'idle'
	identityError.value = null
	identitySavedAt.value = null
})
